<template>
    <div>
        <!-- filter -->
        <v-row class="mt-2 px-2">
            <v-col cols="auto" class="pa-1">
                <v-radio-group
                    v-model="dateType"
                    row
                    class="mt-1"
                    hide-details
                    :disabled="loading"
                >
                    <v-radio
                        :label="$t('daily-date')"
                        value="daily"
                    ></v-radio>
                    <v-radio
                        :label="$t('from-date') + ' / ' + $t('to-date')"
                        value="period"
                    ></v-radio>
                </v-radio-group>
            </v-col>
            <!-- date -->
            <v-col v-if="dateType === 'period'" cols="6" sm="3" md="2"  class="pa-1">
                <bee-date-picker
                    v-model="fromDate"
                    :input-label="$t('inputs.start-date')"
                    menu-picker
                    dense
                    hide-details
                    :input-props="{
                        outlined: true,
                        dense: true,
                        clearable: true
                    }"
                    :allowed-dates="disableDates"
                    :disabled="loading"
                ></bee-date-picker>
            </v-col>
            <!-- end date -->
            <v-col v-if="dateType === 'period'" cols="6" sm="3" md="2"  class="pa-1">
                <bee-date-picker
                    v-model="toDate"
                    :input-label="$t('inputs.end-date')"
                    menu-picker
                    dense
                    hide-details
                    :input-props="{
                        outlined: true,
                        dense: true,
                        clearable: true
                    }"
                    :allowed-dates="disableDates"
                    :disabled="loading"
                ></bee-date-picker>
            </v-col>
            <v-col v-if="dateType === 'daily'" cols="12" sm="6" md="3"  class="pa-1">
                <bee-date-input
                    v-model="date"
                    :append-icon="$i18n.locale === 'en' ? 'mdi-menu-right' : 'mdi-menu-left' "
                    :prepend-icon="$i18n.locale === 'en' ? 'mdi-menu-left' : 'mdi-menu-right' "
                    :label="$t('inputs.date')"
                    input-format-order="DD-MM-YYYY"
                    dense
                    :loading="loading"
                    hide-details
                    @click:append="
                        addDate();
                        needToRestShownItems = true;
                        fetchAll()
                    "
                    @click:prepend="
                        subDate();
                        needToRestShownItems = true;
                        fetchAll()
                    "
                    :disabled="loading"
                ></bee-date-input>
            </v-col>
            <!-- posted -->
            <v-col cols="auto" class="pa-1">
                <v-btn-toggle
                    v-model="posted"
                    mandatory
                    dense
                    class="mb-1"
                    color="primary"
                    @change="needToRestShownItems = true; fetchAll()"
                    :disabled="loading"
                >

                    <v-btn :disabled="loading" :value="null">
                       {{$t('inputs.all')}} 
                    </v-btn>

                    <v-btn :disabled="loading" :value="false">
                       {{$t('inputs.unPosted')}} 
                    </v-btn>

                    <v-btn :disabled="loading" :value="true">
                       {{$t('inputs.posted')}} 
                    </v-btn>
                </v-btn-toggle>
            </v-col>
        
            <v-col>
                <v-btn
                    color="success"
                    class="px-sm-4 mx-2"
                    :loading="loading"
                    :disabled="loading"
                    small
                    @click="needToRestShownItems = true; fetchAll()"
                >
                    <v-icon> mdi-magnify </v-icon>
                    {{$t('search')}}
                </v-btn>

                <v-btn
                    color="alert-color white--text"
                    class="px-sm-8"
                    :disabled="loading"
                    small
                    @click="reset(); needToRestShownItems = true; fetchAll()"
                >
                    {{$t('reset')}}
                </v-btn>
            </v-col>
        </v-row>
        <!-- table -->
        <bee-handy-table
            ref="table"
            :headers="headers"
            :items="[]"
            dense
            hide-default-footer
            pagination-on-scroll
            :pagination-on-scroll-auto-reset-shown="false"
            pagination-on-scroll-server-items
            :items-per-page="20"
            :scroll-options.sync="scrollOptions"
            :server-items-length="serverItemsLength"
            @pagination-on-scroll:reach-last-row="fetchAll"
            :shown-rows-text="$t('shown-rows')"
            class="mt-2"
            :height="$vuetify.breakpoint.height - 190"
            :loading="loading"
            :row-highlight-expression="item => item.isPosted === true"
			row-highlight-expression-class="bg-success"
            fixed-header
        >
            <template v-slot:item.action="{ item, index }">
                <v-btn
                    color="primary"
                    class="px-sm-4 mx-2"
                    :loading="loadingPosted[index]"
                    :disabled="loading || item.isPosted === true"
                    small
                    icon
                    @click="postedInvoice(item.id, index)"
                >
                    <v-icon> {{ item.isPosted ? 'mdi-check-circle' : 'mdi-note-edit' }} </v-icon>
                </v-btn>
            </template>
        </bee-handy-table>
    </div>
</template>

<script>
import moment from 'moment'
import { disableDates } from '@/helpers/functions'
import { isDisabledFriday, enableVacations, enableCustomerType } from '@/configs/global.config'
import { mapState, mapGetters } from 'vuex'

export default {
    data () {
        return { 
            dateType: 'daily',
            loading: false,
            date: moment().format('YYYY-MM-DD'),
            fromDate: moment().startOf('month').format('YYYY-MM-DD'),
            toDate: moment().endOf('month').format('YYYY-MM-DD'),
            posted: false,
            disableDates,
            isDisabledFriday,
            enableVacations,
            enableCustomerType,
            scrollOptions: {},
            serverItemsLength: 0,
            needToRestShownItems: false,
            loadingPosted: [
                false
            ]
            // totalVisits: null
        }
    },

    computed: {
        ...mapState({
            customersTypes: state => state.entityType.customersTypes,
            classification: state => state.workload.classification
        }),

        ...mapGetters({
            getMedicalRepById: 'medicalreps/getMedicalRepById',
            getClassificationById: 'workload/getClassificationById'
        }),

        headers () {
            const headers = [
                { 
                    text: this.$t('headers.employee-name'),
                    value: 'employeeId',
                    setValueExpr: val => this.getMedicalRepById(val)?.name,
                    class: 'primary--text',
                    cellClass: 'primary--text',
                    align: 'start',
                },
                { 
                    text: this.$t('headers.doctor-name'),
                    value: 'entityName',
                    align: 'start',
                    class:'text-capitalize text-body-2 font-weight-bold',
                },
                {
                    text: this.$t('headers.amount-received'),
                    value: 'amountReceived',
                    name: 'amount-received',
                    type: 'number',
                    align: 'start',
                },
                { 
                    text: this.$t('headers.notes'), 
                    value: 'notes', 
                    align: 'center', 
                    noNotAvailableText: true
                },
                {
                    text: this.$t('headers.date'), 
                    value: 'createdAt', 
                    type: 'date',
                    dateFormat: 'YYYY-MM-DD',
                    align: 'start',  
                    cellClass:'text-caption', 
                    columnClass: 'font-sm',
                },
                { 
                    text: '', 
                    name: 'action', 
                    value: 'action',
                    align: 'center', 
                    width: '10%'
                }
            ]
            
            const sharedClass = 'text-body-2 font-weight-bold px-2';

            for (let i = 0; i < headers.length; i++) {
                headers[i].class = headers[i].class ? headers[i].class + ' ' + sharedClass : sharedClass;                
                headers[i].cellClass = headers[i].cellClass ? headers[i].cellClass + ' px-2' : 'px-2';                
            }

            return headers;
        },
    },

    methods: {
        moment,

        addDate () {
            this.date = moment(this.date).add(1, 'day').format('YYYY-MM-DD');
            if (
                (this.moment(this.date).format('dddd') === 'Friday' && this.isDisabledFriday) ||
                (this.enableVacations && this.vacationsDayDetails.includes(this.date))
            ) {
                this.addDate()
            }
            return this.date
        },

        subDate() {
            this.date = moment(this.date).subtract(1, 'day').format('YYYY-MM-DD')
            if (
                (this.moment(this.date).format('dddd') === 'Friday' && this.isDisabledFriday) || 
                (this.enableVacations && this.vacationsDayDetails.includes(this.date))
            ) {
                this.subDate()
            }
            return this.date
        },

        reset () {
            this.date = moment().format('YYYY-MM-DD')
            this.fromDate = moment().startOf('month').format('YYYY-MM-DD')
            this.toDate = moment().endOf('month').format('YYYY-MM-DD')
            this.posted = false
        },

        fetchAll (newOptionsScroll, isFirstLoad = false) {
            this.loading = true;
            let page = 1;

            if (newOptionsScroll) {
                page = newOptionsScroll.page;
            }

            // const { itemsPerPage } = this.scrollOptions;
            return this.$store.dispatch('visits/fetchByFilter', {
                fromDate: this.dateType === 'daily' ? this.date : this.startDate, 
                toDate: this.dateType === 'daily' ? this.date : this.endDate,
                specialtyId: this.specialtyId, 
                workload: this.workload,
                page: page,
                PerPage: 20,
                OrderByName: this.sort === 0,
                OrderByWorkLoad: this.sort === 1,
                regionIds: [],
                employeeIds: [],
                isPosted: this.posted
            })
            .then(({ data, total }) => {
                if (this.needToRestShownItems) {
                    this.$refs.table.resetShownRows();
                    this.needToRestShownItems = false;
                }
                // this.totalVisits = total;
                this.$refs.table.addScrollItems(data);
                this.serverItemsLength = total;
            })
            .finally(() => {
                if (!isFirstLoad) this.loading = false;
            })
        },

        postedInvoice (id, index) {
            this.loadingPosted[index] = true
            // this.loading = true
            this.$store.dispatch('visits/postedInvoice', { id: id, posted: true })
            .then(() => {
                this.needToRestShownItems = true
                this.fetchAll()
                this.$eventBus.$emit('show-snackbar', 'The migration process was completed successfully.')
            }).catch(() => {
                this.$eventBus.$emit('show-snackbar', 'The migration process failed')
            })
            .finally(() => {
                this.loadingPosted[index] = false
            })
        }
    },

    created () {
        this.loading = true
        Promise.all([
            !this.classification.length ? this.$requestStack.setRequest({
                key: 'workload/fetchAll',
                promise: this.$store.dispatch('workload/fetchAll'),
            }) : this.$requestStack.getRequest('workload/fetchAll').promise,

            enableCustomerType && !this.customersTypes.length ? this.$store.dispatch('entityType/fetchAll') : null,
        ]).then(() => {
            this.fetchAll()
        }).finally(() => {
            this.loading = false
        })
    }
}
</script>

<style>
.bg-success {
    background-color: #a8e5c0;
}
</style>
